import { DateTime } from 'luxon';
import { InspectionModel } from 'shared/domain/inspection/inspectionModel';
import { InspectionOutDto } from 'shared/dtos/out/inspection';
import { ObjectIdString } from 'helpers/idComparison';
import { Identificable, LabelledEntity } from 'shared/types/commonView';
import { InspectionFlatForm } from 'views/inspections/wizard/model';

type InspectionHeaderFieldKey = keyof Pick<
  InspectionFlatForm,
  | 'inspectionDate'
  | 'workTypes'
  | 'sites'
  | 'levels'
  | 'controlledParty'
  | 'contracts'
  | 'comment'
>;

const HEADER_FIELD_KEYS = new Set<string>([
  'inspectionDate',
  'workTypes',
  'sites',
  'levels',
  'controlledParty',
  'contracts',
  'comment',
]);

function toIdList(
  entities: LabelledEntity[] | undefined
): ObjectIdString[] | undefined {
  return entities?.map((entity) => entity._id as ObjectIdString);
}

function hasProtocolIssues(protocol: InspectionModel['protocol']): boolean {
  return protocol.some(
    (protocolItem) => protocolItem.complianceCheck.issues.length > 0
  );
}

function toInspectionDateOutDto(
  value: InspectionFlatForm['inspectionDate']
): string | undefined {
  if (DateTime.isDateTime(value)) {
    return value.toISO() ?? undefined;
  }

  if (typeof value === 'string') {
    return value;
  }

  return undefined;
}

export function isInspectionHeaderFieldKey(
  formKey: string
): formKey is InspectionHeaderFieldKey {
  return HEADER_FIELD_KEYS.has(formKey);
}

export function toSingleFieldOutDto(
  formKey: InspectionHeaderFieldKey,
  value: unknown,
  inspectionModel: InspectionModel | undefined
): Partial<InspectionOutDto> | null {
  switch (formKey) {
    case 'inspectionDate':
      return { inspectionDate: toInspectionDateOutDto(value as InspectionFlatForm['inspectionDate']) };
    case 'comment':
      return { comment: typeof value === 'string' ? value : undefined };
    case 'workTypes':
      return { workTypes: toIdList(value as LabelledEntity[]) };
    case 'levels':
      return { levels: toIdList(value as LabelledEntity[]) };
    case 'controlledParty':
      return { controlledParty: toIdList(value as LabelledEntity[]) };
    case 'contracts':
      return { contracts: toIdList(value as LabelledEntity[]) };
    case 'sites':
      if (inspectionModel && hasProtocolIssues(inspectionModel.protocol)) {
        return null;
      }
      return { sites: toIdList(value as LabelledEntity[]) };
    default:
      return null;
  }
}

export function toIdentificableList(
  entities: LabelledEntity[] | undefined
): Identificable<ObjectIdString>[] | undefined {
  return entities?.map((entity) => ({
    _id: entity._id as ObjectIdString,
  }));
}
